import { useCallback, useEffect, useRef, useState } from 'react';

/**
 *
 * @param {Object} params
 * @param {string} params.timeout - timeout in miliseconds.
 * @param {string} params.countdownTime - countdown time in seconds.
 * @param {string} params.onTimeout - callback to execute after countdown.
 * @returns
 */
const useIdleTimer = ({
  timeout,
  countdownTime,
  onTimeout,
}: {
  timeout: number;
  countdownTime: number;
  onTimeout: () => void;
}) => {
  const [isIdle, setIsIdle] = useState(false);
  const [countdown, setCountdown] = useState(countdownTime);
  const idleTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const countdownTimer = useRef<NodeJS.Timeout | undefined>(undefined);

  const resetIdleTimer = useCallback(() => {
    clearTimeout(idleTimer.current);
    clearInterval(countdownTimer.current);
    setIsIdle(false);
    setCountdown(countdownTime);
    addEventListeners();

    idleTimer.current = setTimeout(() => {
      setIsIdle(true);
      removeEventListeners();
      countdownTimer.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownTimer.current);
            onTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, timeout);
  }, []);

  const addEventListeners = () => {
    window.addEventListener('mousemove', resetIdleTimer);
    window.addEventListener('keydown', resetIdleTimer);
    window.addEventListener('touchstart', resetIdleTimer);
    window.addEventListener('scroll', resetIdleTimer);
  };

  const removeEventListeners = () => {
    window.removeEventListener('mousemove', resetIdleTimer);
    window.removeEventListener('keydown', resetIdleTimer);
    window.removeEventListener('touchstart', resetIdleTimer);
    window.removeEventListener('scroll', resetIdleTimer);
  };

  useEffect(() => {
    addEventListeners();
    resetIdleTimer();

    return () => {
      clearTimeout(idleTimer.current);
      clearInterval(countdownTimer.current);
      removeEventListeners();
    };
  }, []);

  return { isIdle, countdown, reset: resetIdleTimer };
};

export { useIdleTimer };
